import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Truck, ShieldCheck, WarningCircle, CheckCircle, Spinner,
  MapPinLine, Path, Clock, Users, ArrowRight,
} from '@phosphor-icons/react';
import logisticsApi from '../../lib/logisticsApi';
import SimCard from './SimCard';

const fmt = (minor) =>
  ((minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Line = ({ label, value, strong, accent, currencySymbol = 'Ÿ' }) => (
  <div className="flex items-baseline justify-between py-1.5">
    <span className="text-sm" style={{ color: strong ? 'var(--ls-secondary)' : 'var(--ls-muted)', fontWeight: strong ? 600 : 400 }}>{label}</span>
    <span className="ls-mono" style={{ fontSize: strong ? 16 : 13, fontWeight: strong ? 700 : 500, color: accent ? 'var(--ls-primary)' : 'var(--ls-secondary)' }}>
      {currencySymbol}{fmt(value)}
    </span>
  </div>
);

const TRIP_TYPES = [
  { key: 'one_way', label: 'One Way', icon: ArrowRight },
  { key: 'return', label: 'Return', icon: Path },
  { key: 'hire_hourly', label: 'Hourly Hire', icon: Clock },
];

const TransportBookingDrawer = ({
  open, onClose, exerciseId, venue, vehicle, routeInfo,
  wallets = [], currencySymbol = 'Ÿ', onBooked,
}) => {
  const [tripType, setTripType] = useState('one_way');
  const [hours, setHours] = useState(2);
  const [passengers, setPassengers] = useState(1);
  const [pickupDatetime, setPickupDatetime] = useState('');
  const [walletId, setWalletId] = useState('');
  const [card, setCard] = useState(null);
  const [cvv, setCvv] = useState('');
  const [notes, setNotes] = useState('');

  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(null);

  const wallet = useMemo(() => wallets.find((w) => w.wallet_id === walletId) || null, [wallets, walletId]);

  useEffect(() => {
    if (walletId || wallets.length === 0) return;
    const best = [...wallets].sort((a, b) => (b.available || 0) - (a.available || 0))[0];
    setWalletId(best.wallet_id);
  }, [wallets, walletId]);

  useEffect(() => {
    if (!open) return;
    setCvv(''); setSubmitError(null); setSuccess(null); setNotes('');
    setPassengers(1); setTripType('one_way'); setHours(2); setPickupDatetime('');
  }, [open, vehicle?.vehicle_id]);

  useEffect(() => {
    if (!walletId) { setCard(null); return; }
    let cancelled = false;
    (async () => {
      try { const c = await logisticsApi.walletSimCard(walletId); if (!cancelled) setCard(c); }
      catch { if (!cancelled) setCard(null); }
    })();
    return () => { cancelled = true; };
  }, [walletId]);

  const fetchQuote = useCallback(async () => {
    if (!open || !exerciseId || !venue?.venue_id || !vehicle?.vehicle_id || !routeInfo) return;
    setQuoting(true); setQuoteError(null);
    try {
      const q = await logisticsApi.quoteTransport(exerciseId, {
        venue_id: venue.venue_id,
        vehicle_id: vehicle.vehicle_id,
        origin_lat: routeInfo.origin.lat,
        origin_lng: routeInfo.origin.lng,
        destination_lat: routeInfo.destination.lat,
        destination_lng: routeInfo.destination.lng,
        trip_type: tripType,
        hours: tripType === 'hire_hourly' ? hours : 0,
        passengers,
      });
      setQuote(q);
    } catch (e) {
      setQuote(null); setQuoteError(e.message || 'Could not price this trip');
    } finally { setQuoting(false); }
  }, [open, exerciseId, venue?.venue_id, vehicle?.vehicle_id, routeInfo, tripType, hours, passengers]);

  useEffect(() => {
    const t = setTimeout(fetchQuote, 300);
    return () => clearTimeout(t);
  }, [fetchQuote]);

  const total = quote?.total_minor || 0;
  const balanceBefore = wallet?.available || 0;
  const balanceAfter = balanceBefore - total;
  const canAfford = balanceAfter >= 0;
  const cvvReady = /^\d{3}$/.test(cvv);
  const ready = !!quote && !quoting && !!walletId && canAfford && cvvReady && !submitting;

  const authorise = async () => {
    if (!ready) return;
    setSubmitting(true); setSubmitError(null);
    try {
      const idem = `trn:${exerciseId}:${venue.venue_id}:${vehicle.vehicle_id}:${walletId}:${Date.now()}`;
      const held = await logisticsApi.createTransportBooking(exerciseId, {
        venue_id: venue.venue_id,
        vehicle_id: vehicle.vehicle_id,
        origin_lat: routeInfo.origin.lat,
        origin_lng: routeInfo.origin.lng,
        origin_name: routeInfo.origin.name || '',
        destination_lat: routeInfo.destination.lat,
        destination_lng: routeInfo.destination.lng,
        destination_name: routeInfo.destination.name || '',
        trip_type: tripType,
        hours: tripType === 'hire_hourly' ? hours : 0,
        passengers,
        pickup_datetime: pickupDatetime || null,
        wallet_id: walletId,
        cvv,
        idempotency_key: idem,
        notes: notes.trim() || null,
      });
      const confirmed = await logisticsApi.confirmTransportBooking(held.booking.booking_id);
      setSuccess(confirmed.booking);
      setCvv('');
      onBooked?.(confirmed.booking, confirmed.wallet);
    } catch (e) {
      setSubmitError(e.message || 'Booking failed');
    } finally { setSubmitting(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }} onClick={onClose}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
          />
          <motion.aside
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="ls-glass fixed top-0 right-0 h-full z-50 flex flex-col w-full sm:w-[480px]"
            style={{ borderLeft: '1px solid var(--ls-border)' }}
            data-testid="ls-transport-booking-drawer"
            role="dialog" aria-label="Transport booking"
          >
            <div className="flex items-center justify-between px-5 h-14 shrink-0" style={{ borderBottom: '1px solid var(--ls-border)' }}>
              <div className="flex items-center gap-2">
                <Truck size={16} weight="fill" style={{ color: 'var(--ls-primary)' }} />
                <span className="ls-heading text-sm font-bold">Book Transport</span>
              </div>
              <button onClick={onClose} className="ls-btn-ghost !p-2" aria-label="Close"><X size={14} weight="bold" /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-5">
              {success ? (
                <div className="text-center py-10" data-testid="ls-transport-booking-success">
                  <CheckCircle size={56} weight="duotone" style={{ color: 'var(--ls-success)' }} />
                  <h3 className="ls-heading text-xl font-bold mt-4">Booking confirmed</h3>
                  <p className="text-sm mt-1" style={{ color: 'var(--ls-muted)' }}>{success.vehicle?.name}</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--ls-muted)' }}>
                    {success.route?.origin?.name || 'Origin'} → {success.route?.destination?.name || 'Destination'}
                  </p>
                  <p className="ls-mono text-3xl font-bold mt-4" style={{ color: 'var(--ls-primary)' }}>
                    {currencySymbol}{fmt(success.total_minor)}
                  </p>
                  <p className="ls-mono text-[11px] mt-3" style={{ color: 'var(--ls-muted)' }}>{success.booking_id}</p>
                  <button className="ls-btn-primary mt-6" onClick={onClose}>Done</button>
                </div>
              ) : (
                <>
                  {/* Vehicle + depot header */}
                  <div className="flex gap-3 mb-4">
                    <div className="w-20 h-16 shrink-0 rounded bg-cover bg-center"
                      style={{ backgroundImage: `url(${vehicle?.photo})`, border: '1px solid var(--ls-border)' }} />
                    <div className="min-w-0">
                      <p className="ls-overline">{(venue?.subcategory || 'car_rental').replace(/_/g, ' ')}</p>
                      <h3 className="ls-heading text-lg font-bold leading-tight truncate">{vehicle?.name}</h3>
                      <p className="text-xs truncate" style={{ color: 'var(--ls-muted)' }}>{venue?.name}</p>
                    </div>
                  </div>

                  {/* Route summary */}
                  {routeInfo && (
                    <div className="ls-elevated p-3 mb-4 rounded-lg" style={{ border: '1px solid var(--ls-border)' }}>
                      <div className="flex items-start gap-2">
                        <div className="flex flex-col items-center mt-1 gap-0.5">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--ls-success)' }} />
                          <div className="w-0.5 flex-1 min-h-[20px]" style={{ background: 'var(--ls-border)' }} />
                          <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--ls-danger)' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="mb-3">
                            <p className="text-[10px] font-semibold" style={{ color: 'var(--ls-muted)' }}>PICKUP</p>
                            <p className="text-sm font-medium truncate" style={{ color: 'var(--ls-secondary)' }}>
                              {routeInfo.origin?.name || 'Origin'}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold" style={{ color: 'var(--ls-muted)' }}>DROP-OFF</p>
                            <p className="text-sm font-medium truncate" style={{ color: 'var(--ls-secondary)' }}>
                              {routeInfo.destination?.name || 'Destination'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="ls-mono text-sm font-bold" style={{ color: 'var(--ls-primary)' }}>
                            {routeInfo.estimated_road_km} km
                          </p>
                          <p className="text-[10px]" style={{ color: 'var(--ls-muted)' }}>
                            ~{routeInfo.estimated_duration_minutes} min
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Trip type */}
                  <div className="mb-4">
                    <span className="ls-overline mb-2 block">Trip type</span>
                    <div className="flex gap-2">
                      {TRIP_TYPES.map(({ key, label, icon: Icon }) => (
                        <button key={key}
                          className="flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all"
                          style={{
                            background: tripType === key ? 'var(--ls-primary)' : 'var(--ls-elevated)',
                            color: tripType === key ? '#fff' : 'var(--ls-muted)',
                            border: `1px solid ${tripType === key ? 'var(--ls-primary)' : 'var(--ls-border)'}`,
                          }}
                          onClick={() => setTripType(key)}
                        >
                          <Icon size={13} weight="bold" /> {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Passengers + hours + pickup */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <label className="block">
                      <span className="ls-overline mb-1 block">Passengers</span>
                      <input type="number" className="ls-input w-full text-sm" min={1} max={vehicle?.capacity || 99}
                        value={passengers} onChange={(e) => setPassengers(Math.max(1, Number(e.target.value) || 1))} />
                      <p className="text-[10px] mt-0.5" style={{ color: 'var(--ls-muted)' }}>Max {vehicle?.capacity}</p>
                    </label>
                    {tripType === 'hire_hourly' ? (
                      <label className="block">
                        <span className="ls-overline mb-1 block">Hours</span>
                        <input type="number" className="ls-input w-full text-sm" min={1} max={24}
                          value={hours} onChange={(e) => setHours(Math.max(1, Number(e.target.value) || 1))} />
                      </label>
                    ) : (
                      <label className="block">
                        <span className="ls-overline mb-1 block">Pickup date/time</span>
                        <input type="datetime-local" className="ls-input w-full text-sm"
                          value={pickupDatetime} onChange={(e) => setPickupDatetime(e.target.value)} />
                      </label>
                    )}
                  </div>

                  {/* Vehicle info */}
                  <div className="ls-elevated p-3 mb-4 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <Users size={14} weight="bold" style={{ color: 'var(--ls-primary)' }} />
                      <span className="text-sm" style={{ color: 'var(--ls-secondary)' }}>
                        {vehicle?.capacity} seats · {vehicle?.luggage} luggage
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {vehicle?.features?.map((f) => (
                        <span key={f} className="text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5"
                          style={{ background: 'var(--ls-surface)', color: 'var(--ls-muted)', border: '1px solid var(--ls-border)' }}>
                          <CheckCircle size={8} weight="fill" style={{ color: 'var(--ls-success)' }} /> {f}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Price breakdown */}
                  <div className="ls-elevated p-4 mb-4 rounded-lg">
                    {quoting ? (
                      <div className="flex items-center gap-2 py-4 justify-center">
                        <Spinner size={14} className="animate-spin" style={{ color: 'var(--ls-primary)' }} />
                        <span className="text-sm" style={{ color: 'var(--ls-muted)' }}>Pricing…</span>
                      </div>
                    ) : quoteError ? (
                      <p className="text-sm py-2" style={{ color: 'var(--ls-danger)' }}>{quoteError}</p>
                    ) : quote ? (
                      <>
                        {quote.pricing && (
                          <div className="text-[11px] mb-2 pb-2" style={{ borderBottom: '1px solid var(--ls-border)', color: 'var(--ls-muted)' }}>
                            {quote.route?.estimated_road_km} km × {currencySymbol}{fmt(quote.pricing.per_km_minor)}/km
                            {tripType === 'return' && ' × 1.85 (return)'}
                            {tripType === 'hire_hourly' && ` + ${hours}h × ${currencySymbol}${fmt(quote.pricing.per_hour_minor)}/hr`}
                          </div>
                        )}
                        <Line label="Subtotal" value={quote.subtotal_minor} currencySymbol={currencySymbol} />
                        <Line label="Simulated tax" value={quote.tax_minor} currencySymbol={currencySymbol} />
                        <Line label="Service fee" value={quote.service_fee_minor} currencySymbol={currencySymbol} />
                        <div style={{ borderTop: '1px solid var(--ls-border)', marginTop: 6, paddingTop: 6 }}>
                          <Line label="Total" value={quote.total_minor} strong accent currencySymbol={currencySymbol} />
                        </div>
                      </>
                    ) : null}
                  </div>

                  {/* Notes */}
                  <label className="block mb-4">
                    <span className="ls-overline mb-1 block">Notes</span>
                    <textarea className="ls-input w-full text-sm" rows={2} placeholder="Special requirements..."
                      value={notes} onChange={(e) => setNotes(e.target.value)} />
                  </label>

                  {/* Wallet picker */}
                  <label className="block mb-4">
                    <span className="ls-overline mb-1 block">Pay from</span>
                    <select value={walletId} onChange={(e) => setWalletId(e.target.value)} className="ls-input w-full">
                      {wallets.length === 0 && <option value="">No wallet available</option>}
                      {wallets.map((w) => (
                        <option key={w.wallet_id} value={w.wallet_id}>
                          {w.owner_type === 'team' ? 'Team' : 'Personal'} · Ÿ{fmt(w.available)} available
                        </option>
                      ))}
                    </select>
                  </label>

                  {/* Card + CVV */}
                  <div className="grid grid-cols-2 gap-3 mb-4 items-start">
                    <SimCard card={card} holder={card?.holder} compact />
                    <div>
                      <span className="ls-overline mb-1 block">Security code</span>
                      <input type="text" inputMode="numeric" maxLength={3}
                        value={cvv} placeholder="•••"
                        onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 3))}
                        className="ls-input w-full ls-mono text-center"
                        style={{ fontSize: 20, letterSpacing: '0.35em' }}
                        aria-label="Card security code"
                      />
                      <p className="text-[11px] mt-2 leading-snug" style={{ color: 'var(--ls-muted)' }}>
                        Read the code off your simulated card
                        {card?.cvv ? <> — <span className="ls-mono">{card.cvv}</span></> : null}.
                      </p>
                    </div>
                  </div>

                  {/* Balance ledger */}
                  {wallet && quote && (
                    <div className="ls-surface p-4 rounded-lg" style={{ borderColor: canAfford ? 'var(--ls-border)' : 'var(--ls-danger)' }}>
                      <p className="ls-overline mb-2">Balance</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[11px]" style={{ color: 'var(--ls-muted)' }}>Before</p>
                          <p className="ls-mono text-lg font-semibold">Ÿ{fmt(balanceBefore)}</p>
                        </div>
                        <span style={{ color: 'var(--ls-muted)' }}>→</span>
                        <div className="text-right">
                          <p className="text-[11px]" style={{ color: 'var(--ls-muted)' }}>After</p>
                          <p className="ls-mono text-lg font-bold"
                            style={{ color: canAfford ? 'var(--ls-primary)' : 'var(--ls-danger)' }}>
                            Ÿ{fmt(balanceAfter)}
                          </p>
                        </div>
                      </div>
                      {!canAfford && (
                        <p className="text-xs mt-3 flex items-center gap-1.5" style={{ color: 'var(--ls-danger)' }}>
                          <WarningCircle size={13} weight="fill" /> Not enough available credit.
                        </p>
                      )}
                    </div>
                  )}

                  {submitError && (
                    <p className="text-sm mt-4 flex items-start gap-1.5" style={{ color: 'var(--ls-danger)' }}>
                      <WarningCircle size={15} weight="fill" className="shrink-0 mt-0.5" /> {submitError}
                    </p>
                  )}
                </>
              )}
            </div>

            {!success && (
              <div className="px-5 py-4 shrink-0" style={{ borderTop: '1px solid var(--ls-border)' }}>
                <button className="ls-btn-primary w-full justify-center" disabled={!ready} onClick={authorise}>
                  {submitting ? (
                    <><Spinner size={15} className="animate-spin" /> Authorising…</>
                  ) : (
                    <><ShieldCheck size={15} weight="fill" /> Authorise {currencySymbol}{fmt(total)}</>
                  )}
                </button>
                <p className="text-[11px] text-center mt-2" style={{ color: 'var(--ls-muted)' }}>
                  Funds are held, then captured on confirmation.
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default TransportBookingDrawer;
